import React, { useState, useMemo, useEffect } from "react";
import {
  DatePicker,
  Dropdown,
  Input,
  Select,
  Table,
  Spin,
  Modal,
  notification,
  Pagination,
} from "antd";
import { debounce } from "lodash";
import { ACTIONS_DROPDOWN_PROPS } from "@/constants/actionsDropdown";
import {
  buildConditionalPagination,
  DEFAULT_PAGINATION_OPTIONS,
  PAGINATION_PAGE_SIZE,
  shouldShowPagination,
} from "@/utils/pagination";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import {
  useGetFormsQuery,
  useGetOrganizationFormsQuery,
  useUpdateDatesActivateMutation,
} from "@/store/api/apiSlice";
import {
  SearchOutlined,
  CloseOutlined,
  MoreOutlined,
  EditOutlined,
  DeleteOutlined,
  ExclamationCircleFilled,
  InboxOutlined,
  CopyOutlined,
  PlayCircleOutlined,
  StopOutlined,
} from "@ant-design/icons";
import { LuFilter } from "react-icons/lu";
import { LayoutGrid, List } from "lucide-react";
import CreateFormModal from "../../../components/Modals/CreateFormModal";
import ActivateDateWarningModal from "../../../components/Modals/ActivateDateWarningModal";
import FormCard from "./components/FormCard";
import StatCard from "./components/StatCard";
import {
  useArchiveFormMutation,
  useDeleteFormMutation,
  useGetFormStatsQuery,
  useGetOrganizationsQuery,
  useUpdateFormStatusMutation,
  useDuplicateFormMutation,
  useActivateFormMutation,
  useDeactivateFormMutation,
  useGetSubmitToOrganizationStatsArrayQuery,
} from "../../../store/api/apiSlice";
import { selectCurrentRole } from "../../../store/auth/authSlice";
import { useSelector } from "react-redux";
import {
  FORM_TABS,
  formMatchesStatusFilter,
  formMatchesTab,
  getFormListTabFromSearch,
  getFormsListOverviewStats,
  sortFormsForDisplayTab,
  getFormOrganizationDisplayName,
  getFormOrganizationLogoUrl,
  isDemoFormType,
  normalizeFormsListResponse,
  withResolvedDemoFields,
} from "../../../utils/formTabs";
import { OrganizationsIcon } from "../../../assets/svgs/icons";
import { getStatusColor, getStatusDisplayText } from "../../../utils";

const INITIAL_FILTERS = {
  status: undefined,
  type: undefined,
  dateRange: undefined,
  organization: undefined,
};

const FORMS_PAGE_SIZE = PAGINATION_PAGE_SIZE;

const Forms = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  const userRole = useSelector(selectCurrentRole);

  const [viewType, setViewType] = useState("list");
  const activeFormTab = getFormListTabFromSearch(searchParams);
  const [cardPage, setCardPage] = useState(1);
  const [cardPageSize, setCardPageSize] = useState(FORMS_PAGE_SIZE);
  const [searchInput, setSearchInput] = useState("");
  const [searchText, setSearchText] = useState("");
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [editingForm, setEditingForm] = useState(null);
  const [filterVisible, setFilterVisible] = useState(false);
  const [filters, setFilters] = useState(INITIAL_FILTERS);
  const [showActivateDateModal, setShowActivateDateModal] = useState(false);
  const [selectedFormForActivation, setSelectedFormForActivation] =
    useState(null);
    
  const isOrganizationContext =
    window.location.pathname.includes("/organisation/");

  const [deleteForm] = useDeleteFormMutation();
  const [updateFormStatus] = useUpdateFormStatusMutation();
  const [activateForm] = useActivateFormMutation();
  const [deactivateForm] = useDeactivateFormMutation();
  const [duplicateForm] = useDuplicateFormMutation();
  const [archiveForm] = useArchiveFormMutation();
  const [updateDatesActivate] = useUpdateDatesActivateMutation();
  const { data: organizationsData, isLoading: orgsLoading } =
    useGetOrganizationsQuery();

  const queryParams = {
    search: searchText,
    type: filters.type,
    organization: filters.organization,
    start_date:
      filters.dateRange?.[0] &&
      String(Math.floor(new Date(filters.dateRange?.[0]).getTime() / 1000)),
    end_date:
      filters.dateRange?.[1] &&
      String(Math.floor(new Date(filters.dateRange?.[1]).getTime() / 1000)),
  };

  const {
    data: formsData,
    isLoading,
    isFetching,
    refetch,
  } = isOrganizationContext
    ? useGetOrganizationFormsQuery(queryParams, {
        refetchOnMountOrArgChange: true,
      })
    : useGetFormsQuery(queryParams, {
        refetchOnMountOrArgChange: true,
      });

  const { data: statsArrayData, isLoading: statsArrayLoading } =
    useGetSubmitToOrganizationStatsArrayQuery(undefined, {
      skip: userRole !== "organization",
    });

  const statsMap = useMemo(() => {
    const formStats = Array.isArray(statsArrayData?.data)
      ? statsArrayData.data
      : Array.isArray(statsArrayData?.data?.forms)
      ? statsArrayData.data.forms
      : [];

    if (!formStats.length) return {};

    const map = {};
    formStats.forEach((stat) => {
      if (stat.form_id) {
        map[stat.form_id] = stat;
      }
    });

    return map;
  }, [statsArrayData]);

  const forms = useMemo(() => {
    return normalizeFormsListResponse(formsData, isOrganizationContext).map(
      withResolvedDemoFields,
    );
  }, [formsData, isOrganizationContext]);

  const tabbedForms = useMemo(() => {
    const filtered = forms
      .filter((form) => {
        if (!formMatchesTab(form, activeFormTab)) return false;

        return formMatchesStatusFilter(form, filters.status);
      })
      .map(withResolvedDemoFields);

    return sortFormsForDisplayTab(filtered, activeFormTab);
  }, [forms, activeFormTab, filters.status]);

  const paginatedCardForms = useMemo(() => {
    if (tabbedForms.length <= FORMS_PAGE_SIZE) return tabbedForms;

    const start = (cardPage - 1) * cardPageSize;
    return tabbedForms.slice(start, start + cardPageSize);
  }, [tabbedForms, cardPage, cardPageSize]);

  const statsQueryParams = useMemo(
    () => ({
      organization: filters.organization,
      status: filters.status,
      type: filters.type,
      search: searchText,
      start_date:
        filters.dateRange?.[0] &&
        String(Math.floor(new Date(filters.dateRange?.[0]).getTime() / 1000)),
      end_date:
        filters.dateRange?.[1] &&
        String(Math.floor(new Date(filters.dateRange?.[1]).getTime() / 1000)),
    }),
    [
      filters.organization,
      filters.status,
      filters.type,
      filters.dateRange,
      searchText,
    ],
  );

  const { data: adminStatsData, isLoading: adminStatsLoading } =
    useGetFormStatsQuery(statsQueryParams, {
      skip: userRole === "organization",
      refetchOnMountOrArgChange: false,
    });

  const statsData = useMemo(() => {
    const baseStats =
      userRole === "organization"
        ? statsArrayData?.data || statsArrayData
        : adminStatsData;

    return {
      ...(baseStats && !Array.isArray(baseStats) ? baseStats : {}),
      ...getFormsListOverviewStats(forms),
    };
  }, [userRole, statsArrayData, adminStatsData, forms]);

  const statsLoading =
    userRole === "organization" ? statsArrayLoading : adminStatsLoading;
  const basePath = window.location.pathname.split("/")[1];

  const debouncedSearch = useMemo(
    () =>
      debounce((value) => {
        setSearchText(value);
      }, 500),
    [],
  );

  const getResolvedFormRecord = (record) => {
    if (!record?._id) {
      return withResolvedDemoFields(record);
    }

    const fromList = forms.find((form) => form._id === record._id);
    return withResolvedDemoFields(fromList || record);
  };

  const handleEdit = (record) => {
    setEditingForm(getResolvedFormRecord(record));
    setEditModalVisible(true);
  };

  const toUnixSecondsString = (dateStr) => {
    if (!dateStr) return null;
    return String(Math.floor(new Date(dateStr).getTime() / 1000));
  };

  const getFormQuestionCount = (record) => {
    if (Array.isArray(record?.questions)) {
      return record.questions.length;
    }

    if (typeof record?.total_questions === "number") {
      return record.total_questions;
    }

    const stats = statsMap[record?._id];
    if (typeof stats?.total_questions === "number") {
      return stats.total_questions;
    }

    return 0;
  };

  const warnIfFormCannotBeActivated = (record) => {
    if (activeFormTab !== "build") {
      return false;
    }

    if (getFormQuestionCount(record) > 0) {
      return false;
    }

    notification.warning({
      message: "Cannot activate form",
      description: "Add at least one question before activating this form.",
    });

    return true;
  };

  const handleStatusChange = async (record, newStatus) => {
    try {
      if (newStatus === "active") {
        if (warnIfFormCannotBeActivated(record)) {
          return;
        }

        if (!record.start_date || !record.end_date) {
          notification.error({
            message: "Missing Dates",
            description: "Form must have start and end dates to be activated",
          });
          return;
        }

        const startDate = new Date(record.start_date);
        const endDate = new Date(record.end_date);
        const today = new Date();

        if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
          console.error("Invalid dates:", {
            startDate: record.start_date,
            endDate: record.end_date,
          });
          notification.error({
            message: "Invalid Dates",
            description: "Form has invalid start or end dates",
          });
          return;
        }

        const toYMD = (d) => {
          const year = d.getFullYear();
          const month = String(d.getMonth() + 1).padStart(2, "0");
          const day = String(d.getDate()).padStart(2, "0");
          return `${year}-${month}-${day}`;
        };

        const todayStr = toYMD(today);
        const startStr = toYMD(startDate);
        const endStr = toYMD(endDate);

        const isWithinRange = todayStr >= startStr && todayStr <= endStr;

        if (!isWithinRange) {
          setSelectedFormForActivation(record);
          setShowActivateDateModal(true);
          return;
        }

        await activateForm(record._id).unwrap();
      } else if (newStatus === "not_active") {
        await deactivateForm(record._id).unwrap();
      } else {
        await updateFormStatus({
          id: record._id,
          status: newStatus,
        }).unwrap();
        notification.success({
          message: "Success",
          description: `Form status updated to ${newStatus}`,
        });
      }
    } catch (error) {
      if (newStatus !== "active" && newStatus !== "not_active") {
        notification.error({
          message: "Error",
          description: error.data?.message || "Failed to update status",
        });
      }
    }
  };

  const handleConfirmActivationWithDates = async ({ startDate, endDate }) => {
    if (!selectedFormForActivation) return;

    if (warnIfFormCannotBeActivated(selectedFormForActivation)) {
      return;
    }

    try {
      if (!startDate || !endDate) {
        notification.error({
          message: "Validation Error",
          description: "Please select both start and end dates",
        });
        return;
      }

      const startDateObj = new Date(startDate);
      const endDateObj = new Date(endDate);

      if (startDateObj >= endDateObj) {
        notification.error({
          message: "Validation Error",
          description: "End date must be after start date",
        });
        return;
      }

      await updateDatesActivate({
        id: selectedFormForActivation._id,
        startDate: toUnixSecondsString(startDate),
        endDate: toUnixSecondsString(endDate),
      }).unwrap();

      refetch();
    } catch (error) {
      console.error("Error updating form dates and activating:", error);
    
      if (error?.status === 400) {
        notification.error({
          message: "Validation Error",
          description:
            error?.data?.message || "Invalid date range or form data",
        });
      } else if (error?.status === 404) {
        notification.error({
          message: "Form Not Found",
          description: "The form you're trying to activate no longer exists",
        });
      } else if (error?.status === 500) {
        notification.error({
          message: "Server Error",
          description:
            "An error occurred on the server. Please try again later.",
        });
      } else if (error?.status === 401 || error?.status === 403) {
        notification.error({
          message: "Permission Denied",
          description: "You don't have permission to activate this form",
        });
      } else {
        notification.error({
          message: "Activation Failed",
          description:
            error?.data?.message ||
            "Failed to update form dates and activate. Please try again.",
        });
      }
    } finally {
      setShowActivateDateModal(false);
      setSelectedFormForActivation(null);
    }
  };

  const handleArchiveAction = async (record) => {
    try {
      await archiveForm(record._id).unwrap();
      notification.success({
        message: "Success",
        description: "Form added to archive successfully",
      });
    } catch (error) {
      notification.error({
        message: "Error",
        description: error.message || "Failed to update archive status",
      });
    }
  };

  const handleFilterChange = (type, value) => {
    setFilters((prev) => ({
      ...prev,
      [type]: value,
    }));
  };

  const handleDuplicate = async (record) => {
    duplicateForm(record._id).unwrap();
  };

  const handleDelete = (record) => {
    Modal.confirm({
      className: "forms-delete-confirm-modal",
      centered: true,
      icon: <ExclamationCircleFilled />,
      title: "Are you sure you want to delete this form?",
      content: (
        <span className="forms-delete-confirm-modal__message">
          This action cannot be undone.
        </span>
      ),
      okText: "Delete",
      okType: "primary",
      cancelText: "Cancel",
      onOk: async () => {
        try {
          await deleteForm(record._id).unwrap();
          notification.success({
            message: "Success",
            description: "Form deleted successfully",
          });
        } catch (error) {
          notification.error({
            message: "Error",
            description: error.data?.message || "Failed to delete form",
          });
        }
      },
    });
  };
 
  const handleFormTabChange = (tab) => {
    if (tab === activeFormTab) return;

    const nextParams = new URLSearchParams(searchParams);
    if (tab === FORM_TABS.BUILD) {
      nextParams.delete("tab");
    } else {
      nextParams.set("tab", tab);
    }
    setSearchParams(nextParams);
  };

  const handleRowClick = (record) => {
    navigate("/" + basePath + `/forms/${record._id}`, {
      state: {
        ...getResolvedFormRecord(record),
        fromTab: activeFormTab,
        fromPath: `${location.pathname}${location.search}`,
      },
    });
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchInput(value);
    debouncedSearch(value);
  };

  const clearSearch = () => {
    setSearchInput("");
    setSearchText("");
    debouncedSearch.cancel();
  };

  const getActionsMenuItems = (record) => {
    const startLabel = activeFormTab === "build" ? "Activate" : "Start";
    const items = [
      { key: "edit", label: "Edit", icon: <EditOutlined /> },
      { key: "archive", label: "Archive", icon: <InboxOutlined /> },
    ];

    if (record.status === "active") {
      items.push({ key: "end", label: "End", icon: <StopOutlined /> });
    } else if (record.status === "draft") {
      items.push({ key: "start", label: startLabel, icon: <PlayCircleOutlined /> });
    } else {
      items.push({ key: "duplicate", label: "Duplicate", icon: <CopyOutlined /> });
      items.push({ key: "start", label: startLabel, icon: <PlayCircleOutlined /> });
    }

    items.push({
      key: "delete",
      label: "Delete",
      danger: true,
      icon: <DeleteOutlined />,
    });

    return items;
  };

  const handleActionMenuClick = (record, actionKey, event) => {
    event?.domEvent?.stopPropagation?.();

    switch (actionKey) {
      case "edit":
        handleEdit(record);
        break;
      case "delete":
        handleDelete(record);
        break;
      case "archive":
        handleArchiveAction(record);
        break;
      case "end":
        handleStatusChange(record, "not_active");
        break;
      case "start":
        handleStatusChange(record, "active");
        break;
      case "duplicate":
        handleDuplicate(record);
        break;
      default:
        break;
    }
  };

  const renderOrganizationLogoCell = (record) => {
    const logoUrl = getFormOrganizationLogoUrl(record);
    const name = getFormOrganizationDisplayName(record);

    return (
      <div className="flex min-w-0 items-center gap-3">
        <div className="relative flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-full border border-gray-200 bg-gray-50">
          {!logoUrl && <OrganizationsIcon />}
          {logoUrl ? (
            <img
              src={logoUrl}
              alt={name || "Organisation logo"}
              className="h-full w-full object-cover"
              onError={(e) => {
                e.currentTarget.style.display = "none";
              }}
            />
          ) : null}
        </div>
        <span className="min-w-0 truncate font-medium text-gray-900">
          {name || "N/A"}
        </span>
      </div>
    );
  };

  const organizationLogoColumn = {
    title: "",
    key: "organizationLogo",
    width: 220,
    render: (_, record) => renderOrganizationLogoCell(record),
  };

  const formatFormTypeLabel = (type) => {
    const normalized = String(type || "")
      .trim()
      .toLowerCase();
    if (!normalized) return "—";

    const labels = {
      survey: "Survey",
      form: "Form",
      complaint: "Complaint",
      enquiry: "Enquiry",
      demo: "Demo",
    };

    return labels[normalized] || normalized.charAt(0).toUpperCase() + normalized.slice(1);
  };

  const responsesColumn = {
    title: "Responses",
    key: "responses",
    render: (_, record) => {
      if (
        isOrganizationContext &&
        (record.submit_organization === null ||
          record.submit_organization === undefined)
      ) {
        return <span>0(0.0%)</span>;
      }

      const stats = statsMap[record._id];

      const totalQuestions =
        isOrganizationContext && stats
          ? stats.total_questions
          : isOrganizationContext
            ? 0
            : record.questions.length;
      const totalResponses =
        isOrganizationContext && stats
          ? stats.responses_received
          : isOrganizationContext
            ? 0
            : record.total_responses;
      const uniqueRespondents = Array.isArray(record.responses)
        ? new Set(record.responses.map((r) => r.user)).size
        : 0;
      const responsePercentage =
        isOrganizationContext && stats
          ? Number(stats.response_ratio).toFixed(1)
          : isOrganizationContext
            ? "0.0"
            : Number(
                record.response_ratio ??
                  record.response_rate ??
                  (totalQuestions && uniqueRespondents
                    ? (totalResponses / (totalQuestions * uniqueRespondents)) *
                      100
                    : 0),
              ).toFixed(1);

      return (
        <span>
          {totalResponses}({responsePercentage}
          %)
        </span>
      );
    },
  };

  const columns = [
    {
      title: "Sr#",
      dataIndex: "srNo",
      key: "srNo",
      width: 60,
      render: (_, __, index) => index + 1,
    },
    organizationLogoColumn,
    {
      title: "Form",
      key: "form",
      render: (_, record) => (
        <div className="flex flex-col" key={record._id}>
          <div className="font-medium text-gray-900">{record.title}</div>
          <div className="text-sm text-gray-500 truncate max-w-md">
            {record.description}
          </div>
          {!isDemoFormType(record) && activeFormTab !== "build" && (
            <span className="inline-flex px-3 py-2 rounded-full text-xs border border-black text-gray-800 w-fit mt-1">
              {formatFormTypeLabel(record.type)}
            </span>
          )}
        </div>
      ),
    },
    {
      title: "Questions",
      dataIndex: "total_questions",
      key: "total_questions",
      render: (_, record) => <span>{record?.questions?.length || 0}</span>,
    },
    responsesColumn,
    {
      title: "Status",
      key: "status",
      render: (_, record) => (
        <span
          className={`inline-flex px-3 py-1 rounded-full text-sm capitalize ${getStatusColor(
            record.status,
          )}`}
        >
          {getStatusDisplayText(record.status)}
        </span>
      ),
    },
    ...(userRole === "admin"
      ? [
          {
            title: "",
            key: "actions",
            width: 80,
            align: "center",
            render: (_, record) => (
              <div
                className="flex items-center gap-2"
                onClick={(e) => e.stopPropagation()}
              >
                <Dropdown
                  {...ACTIONS_DROPDOWN_PROPS}
                  menu={{
                    items: getActionsMenuItems(record),
                    onClick: (event) =>
                      handleActionMenuClick(record, event.key, event),
                  }}
                >
                  <button
                    type="button"
                    className="actions-dots-btn"
                    onClick={(e) => e.stopPropagation()}
                    aria-label={`Open actions for ${record?.title || "form"}`}
                  >
                    <MoreOutlined />
                  </button>
                </Dropdown>
              </div>
            ),
          },
        ]
      : []),
  ];

  useEffect(() => {
    debouncedSearch.cancel();
    setSearchInput("");
    setSearchText("");
    setFilters(INITIAL_FILTERS);
    setCardPage(1);
  }, [activeFormTab, debouncedSearch]);

  useEffect(() => {
    setCardPage(1);
  }, [filters, searchText, viewType]);
  
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center mb-0 selectSecondary">
        <h3 className="text-md font-semibold m-0">Overview</h3>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-2">
        <StatCard
          value={statsLoading ? <Spin size="small" /> : statsData?.total}
          label="Total Forms"
        />
        <StatCard
          value={statsLoading ? <Spin size="small" /> : statsData?.active}
          label="Active Forms"
        />
        <StatCard
          value={statsLoading ? <Spin size="small" /> : statsData?.complete}
          label="Completed Forms"
        />
        <StatCard
          value={
            statsLoading ? <Spin size="small" /> : statsData?.totalResponses
          }
          label="Total Responses"
        />
        <StatCard
          value={
            statsLoading ? (
              <Spin size="small" />
            ) : (
              `${Number(statsData?.responseRate || 0).toFixed(1)}%`
            )
          }
          label="Response Rate"
        />
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <div className="flex bg-secondary rounded-xl p-1.5">
          <button
            type="button"
            className={`view-toggle-btn ${
              viewType === "list" ? "bg-primary text-white" : ""
            }`}
            onClick={() => setViewType("list")}
          >
            <List size={20} />
          </button>
          <button
            type="button"
            className={`view-toggle-btn ${
              viewType === "grid" ? "bg-primary text-white" : ""
            }`}
            onClick={() => setViewType("grid")}
          >
            <LayoutGrid size={20} />
          </button>
        </div>
        <div className="relative flex-1">
          <Input
            placeholder="Search..."
            value={searchInput}
            prefix={<SearchOutlined />}
            suffix={
              searchInput && (
                <CloseOutlined
                  className="cursor-pointer hover:text-gray-600"
                  onClick={clearSearch}
                />
              )
            }
            onChange={handleSearchChange}
            className="h-[48px] !rounded-2xl bg-white"
          />
        </div>
        <Select
          placeholder="Organisation"
          value={filters.organization}
          onChange={(value) => handleFilterChange("organization", value)}
          className="w-full md:w-48 h-[48px] !rounded-2xl"
          allowClear
          loading={orgsLoading}
          options={
            organizationsData?.data?.map((org) => ({
              value: org._id,
              label: org.name,
            })) || []
          }
        />

        {activeFormTab === "live" || activeFormTab === "unzippd" ? (
          <Select
            allowClear
            value={filters.status}
            onChange={(value) => handleFilterChange("status", value)}
            className="w-[48%] md:w-40 h-[48px] !rounded-2xl"
            placeholder="Status"
            options={[
              { value: "active", label: "Active" },
              {
                value: "complete",
                label: "Completed",
              },
            ]}
          />
        ) : null}

        <Select
          placeholder="Type"
          value={filters.type}
          onChange={(value) => handleFilterChange("type", value)}
          className="w-[48%] md:w-40 h-[48px] !rounded-2xl"
          allowClear
          options={[
            { value: "survey", label: "Survey" },
            { value: "form", label: "Form" },
            {
              value: "complaint",
              label: "Complaint",
            },
            { value: "enquiry", label: "Enquiry" },
            { value: "demo", label: "Demo" },
            { value: "panel", label: "Panel" },
          ]}
        />

        <DatePicker.RangePicker
          value={filters.dateRange}
          className="w-full md:w-[340px] h-[48px] !rounded-2xl"
          onChange={(dates) => handleFilterChange("dateRange", dates)}
          placeholder={["Start date", "End date"]}
          popupClassName="date-range-popup-mobile"
          getPopupContainer={() => document.body}
          dropdownAlign={{
            points: ["tl", "bl"],
            offset: [0, 4],
            overflow: {
              adjustX: 1,
              adjustY: 1,
            },
          }}
        />

        <button
          className={`h-[48px] w-11 flex items-center justify-center rounded-xl ${
            filterVisible ? "bg-primary text-white" : "bg-secondary"
          } border`}
          onClick={() => setFilterVisible(!filterVisible)}
        >
          <LuFilter
            size={20}
            className={filterVisible ? "text-white" : "text-red-500"}
          />
        </button>
      </div>
      <div className="flex items-center rounded-2xl bg-secondary p-1 w-fit">
        <button
          type="button"
          onClick={() => handleFormTabChange("build")}
          className={`px-4 py-2 rounded-xl text-sm font-medium border-0 cursor-pointer ${
            activeFormTab === "build"
              ? "bg-white text-black shadow-sm"
              : "bg-transparent text-gray-600"
          }`}
        >
          Building
        </button>
        <button
          type="button"
          onClick={() => handleFormTabChange("live")}
          className={`px-4 py-2 rounded-xl text-sm font-medium border-0 cursor-pointer ${
            activeFormTab === "live"
              ? "bg-white text-black shadow-sm"
              : "bg-transparent text-gray-600"
          }`}
        >
          Live and Completed
        </button>
        <button
          type="button"
          onClick={() => handleFormTabChange("demo")}
          className={`px-4 py-2 rounded-xl text-sm font-medium border-0 cursor-pointer ${
            activeFormTab === "demo"
              ? "bg-white text-black shadow-sm"
              : "bg-transparent text-gray-600"
          }`}
        >
          Demo
        </button>
        <button
          type="button"
          onClick={() => handleFormTabChange("unzippd")}
          className={`px-4 py-2 rounded-xl text-sm font-medium border-0 cursor-pointer ${
            activeFormTab === "unzippd"
              ? "bg-white text-black shadow-sm"
              : "bg-transparent text-gray-600"
          }`}
        >
          Unzippd
        </button>
      </div>
      {isFetching ? (
        <div className="flex justify-center p-8">
          <Spin />
        </div>
      ) : viewType === "list" ? (
        <Table
          rowKey="_id"
          columns={
            activeFormTab === "build"
              ? columns.filter((column) => column.key !== "status")
              : columns
          }
          dataSource={tabbedForms}
          pagination={buildConditionalPagination(tabbedForms)}
          className="forms-table"
          rowClassName="hover:bg-gray-50 cursor-pointer"
          onRow={(record) => ({
            onClick: () => handleRowClick(record),
          })}
          scroll={{ x: "100%" }}
        />
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
            {paginatedCardForms.map((item) => (
              <FormCard
                key={item._id}
                data={item}
                userRole={userRole}
                isDemoTab={activeFormTab === "demo"}
                onRowClick={handleRowClick}
                getActionsMenuItems={getActionsMenuItems}
                onActionMenuClick={handleActionMenuClick}
              />
            ))}
          </div>
          {shouldShowPagination(tabbedForms) && (
            <Pagination
              className="mt-4 flex justify-end"
              current={cardPage}
              pageSize={cardPageSize}
              total={tabbedForms.length}
              showSizeChanger
              pageSizeOptions={DEFAULT_PAGINATION_OPTIONS.pageSizeOptions}
              showTotal={(total) => `Total ${total} items`}
              onChange={(page, size) => {
                setCardPage(page);
                setCardPageSize(size);
              }}
            />
          )}
        </>
      )}
      {editModalVisible && (
        <CreateFormModal
          visible
          onClose={() => {
            setEditModalVisible(false);
            setEditingForm(null);
          }}
          refetch={refetch}
          editData={editingForm}
        />
      )}
      {showActivateDateModal && (
        <ActivateDateWarningModal
          visible
          onCancel={() => {
            setShowActivateDateModal(false);
            setSelectedFormForActivation(null);
          }}
          onSave={handleConfirmActivationWithDates}
          initialStartDate={
            selectedFormForActivation?.start_date
              ? new Date(parseInt(selectedFormForActivation.start_date) * 1000)
                  .toISOString()
                  .split("T")[0]
              : new Date().toISOString().split("T")[0]
          }
          initialEndDate={
            selectedFormForActivation?.end_date
              ? new Date(parseInt(selectedFormForActivation.end_date) * 1000)
                  .toISOString()
                  .split("T")[0]
              : new Date().toISOString().split("T")[0]
          }
        />
      )}
    </div>
  );
};

export default Forms;
